import { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { toastStore } from './toast-store';
import type { ToastItem as ToastItemType, ToastType } from './types';

// ─── Animations ───────────────────────────────────────────────────────────────

const slideIn = keyframes`
  from {
    transform: translateX(110%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
    max-height: 200px;
    margin-bottom: 8px;
  }
  to {
    transform: translateX(110%);
    opacity: 0;
    max-height: 0;
    margin-bottom: 0;
  }
`;

const progressShrink = keyframes`
  from { width: 100%; }
  to   { width: 0%; }
`;

// ─── Theme ────────────────────────────────────────────────────────────────────

const TYPE_COLORS: Record<ToastType, { border: string; icon: string; progress: string }> = {
  success: { border: '#16a34a', icon: '#16a34a', progress: '#16a34a' },
  error:   { border: '#dc2626', icon: '#dc2626', progress: '#dc2626' },
  warning: { border: '#d97706', icon: '#d97706', progress: '#d97706' },
  info:    { border: '#2563eb', icon: '#2563eb', progress: '#2563eb' },
  loading: { border: '#6b7280', icon: '#6b7280', progress: '#6b7280' },
  default: { border: '#374151', icon: '#374151', progress: '#374151' },
};

const TYPE_ICONS: Record<ToastType, string> = {
  success: '✓',
  error:   '✕',
  warning: '!',
  info:    'i',
  loading: '…',
  default: '•',
};

// ─── Styled Components ────────────────────────────────────────────────────────

const Card = styled.div<{ $type: ToastType; $leaving: boolean }>`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px 18px 16px;
  background: #ffffff;
  border-radius: 10px;
  border-left: 4px solid ${({ $type }) => TYPE_COLORS[$type].border};
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12), 0 1px 6px rgba(0, 0, 0, 0.06);
  width: 360px;
  max-width: calc(100vw - 32px);
  overflow: hidden;
  cursor: default;
  will-change: transform, opacity;

  ${({ $leaving }) =>
    $leaving
      ? css`
          animation: ${slideOut} 260ms cubic-bezier(0.4, 0, 1, 1) forwards;
        `
      : css`
          animation: ${slideIn} 300ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
        `}
`;

const IconBubble = styled.div<{ $type: ToastType }>`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${({ $type }) => TYPE_COLORS[$type].border}18;
  color: ${({ $type }) => TYPE_COLORS[$type].icon};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  margin-top: 1px;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const Message = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  line-height: 1.4;
  word-break: break-word;
`;

const Description = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
  word-break: break-word;
`;

const ActionButton = styled.button`
  margin-top: 8px;
  padding: 4px 10px;
  min-height: 32px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #ffffff;
  color: #1f5b45;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: #1f5b45;
  }

  &:focus-visible {
    outline: 2px solid #1f5b45;
    outline-offset: 2px;
  }
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  padding: 0;
  font-size: 16px;
  line-height: 1;
  transition: color 0.15s, background 0.15s;
  margin-top: 1px;

  &:hover {
    color: #374151;
    background: #f3f4f6;
  }
`;

const ProgressBar = styled.div<{ $type: ToastType; $duration: number; $paused: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: ${({ $type }) => TYPE_COLORS[$type].progress};
  border-radius: 0 0 0 6px;
  animation: ${progressShrink} ${({ $duration }) => $duration}ms linear forwards;
  animation-play-state: ${({ $paused }) => ($paused ? 'paused' : 'running')};
`;

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  item: ToastItemType;
}

export function ToastItem({ item }: Props) {
  const [paused, setPaused] = useState(false);
  const pauseStart = useRef<number | null>(null);
  const remaining = useRef<number>(
    item.duration === false ? Infinity : item.duration,
  );
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
    };
  }, []);

  const handleMouseEnter = () => {
    if (item.duration === false || item.leaving) return;
    setPaused(true);
    pauseStart.current = Date.now();
  };

  const handleMouseLeave = () => {
    if (item.duration === false || item.leaving || pauseStart.current === null) return;
    const elapsed = Date.now() - pauseStart.current;
    remaining.current = Math.max(0, remaining.current - elapsed);
    pauseStart.current = null;
    setPaused(false);

    if (remaining.current > 0) {
      dismissTimer.current = setTimeout(
        () => { toastStore.dismiss(item.id); },
        remaining.current,
      );
    } else {
      toastStore.dismiss(item.id);
    }
  };

  return (
    <Card
      $type={item.type}
      $leaving={item.leaving}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="alert"
      aria-live="assertive"
    >
      <IconBubble $type={item.type}>{TYPE_ICONS[item.type]}</IconBubble>

      <Content>
        <Message>{item.message}</Message>
        {item.description ? <Description>{item.description}</Description> : null}
        {item.action ? (
          <ActionButton
            type="button"
            onClick={() => {
              item.action?.onSelect();
              toastStore.dismiss(item.id);
            }}
          >
            {item.action.label}
          </ActionButton>
        ) : null}
      </Content>

      <CloseButton
        type="button"
        aria-label="Fechar"
        onClick={() => { toastStore.dismiss(item.id); }}
      >
        ×
      </CloseButton>

      {item.duration !== false && (
        <ProgressBar
          $type={item.type}
          $duration={item.duration}
          $paused={paused}
        />
      )}
    </Card>
  );
}
